import type { CardData } from '@/contexts/CardContext';
import { formatAmount, getUnitSymbol } from '@/lib/cashu';
import { BACK_COVER_URL, REDEEM_IMAGE_URL } from '@/lib/presetImages';

interface PrintCardProps {
  card: CardData;
}

/**
 * Foldable A7 greeting card.
 * Prints on TWO sides of a single sheet (10" x 7" landscape each side).
 * Fold the sheet in half to create a 5" x 7" greeting card.
 *
 * SIDE 1 (Outside):
 *   Left half  = Back cover (fixed branding image, rotated 180deg for fold)
 *   Right half = Front cover (user-chosen photo)
 *
 * SIDE 2 (Inside):
 *   Left half  = QR code page (redeem instructions)
 *   Right half = User-chosen inside image with message/names overlay
 */
export function PrintCard({ card }: PrintCardProps) {
  return (
    <>
      <PrintOutside card={card} />
      <PrintInside card={card} />
    </>
  );
}

/** Side 1: Outside (fixed back cover + user front cover) */
function PrintOutside({ card }: PrintCardProps) {
  return (
    <div className="print-page" style={{
      width: '10in',
      height: '7in',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden',
      position: 'relative',
      background: '#fff',
    }}>
      {/* Left = Back cover (always the fixed branding image, rotated 180deg) */}
      <div style={{
        width: '5in',
        height: '7in',
        overflow: 'hidden',
        position: 'relative',
        transform: 'rotate(180deg)',
        background: '#fff',
      }}>
        <img
          src={BACK_COVER_URL}
          alt=""
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>

      {/* Right = Front cover (user-chosen photo) */}
      <div style={{
        width: '5in',
        height: '7in',
        overflow: 'hidden',
        position: 'relative',
        background: '#000',
      }}>
        {card.photoUrl ? (
          <img
            src={card.photoUrl}
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #f6d365, #fda085)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            fontWeight: 700,
            color: '#1a1a2e',
            fontFamily: '"Playfair Display", Georgia, serif',
          }}>
            Cashu Card
          </div>
        )}
      </div>
    </div>
  );
}

/** Side 2: Inside (QR redeem page + inside image with personalization) */
function PrintInside({ card }: PrintCardProps) {
  const isSerif = card.theme.fontStyle === 'serif';
  const fontFamily = isSerif
    ? '"Playfair Display", Georgia, serif'
    : '"Outfit Variable", Outfit, system-ui, sans-serif';

  const hasPersonalization = card.recipientName || card.message || card.senderName;

  return (
    <div className="print-page" style={{
      width: '10in',
      height: '7in',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden',
      position: 'relative',
      background: '#fff',
    }}>
      {/* Left = QR code page */}
      <div style={{
        width: '5in',
        height: '7in',
        overflow: 'hidden',
        position: 'relative',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        padding: '0.4in 0.5in',
      }}>
        {/* Top: Redeem text */}
        <div style={{
          textAlign: 'center',
          fontFamily: '"Outfit Variable", Outfit, system-ui, sans-serif',
        }}>
          <p style={{ fontSize: '13px', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
            Learn How To Redeem At CashuCards.com
          </p>
        </div>

        {/* Middle: QR code */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.1in',
        }}>
          {card.qrDataUrl && (
            <div style={{
              padding: '0.08in',
              borderRadius: '10px',
              backgroundColor: '#ffffff',
              border: '2px solid #e5e7eb',
            }}>
              <img
                src={card.qrDataUrl}
                alt="QR"
                style={{
                  width: '2.8in',
                  height: '2.8in',
                  display: 'block',
                  imageRendering: 'pixelated',
                }}
              />
            </div>
          )}
          <div style={{
            fontSize: '9px',
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            opacity: 0.5,
            fontWeight: 600,
            fontFamily: '"Outfit Variable", Outfit, system-ui, sans-serif',
            color: '#1a1a2e',
          }}>
            Scan to redeem eCash
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 14px',
            borderRadius: '999px',
            fontSize: '13px',
            fontWeight: 700,
            color: '#b8860b',
            backgroundColor: '#fef3c7',
            border: '1.5px solid #fde68a',
          }}>
            <span style={{ fontSize: '15px' }}>{getUnitSymbol(card.unit)}</span>
            <span>{formatAmount(card.amount, card.unit)}</span>
          </div>
        </div>

        {/* Bottom: Cashu Cards logo */}
        <div style={{ textAlign: 'center' }}>
          <img
            src={REDEEM_IMAGE_URL}
            alt="Cashu Cards"
            style={{ width: '1.2in', height: 'auto', opacity: 0.85 }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      </div>

      {/* Right = Inside image with personalization overlay */}
      <div style={{
        width: '5in',
        height: '7in',
        overflow: 'hidden',
        position: 'relative',
        fontFamily,
        color: card.theme.textColor,
        boxSizing: 'border-box',
      }}>
        {/* Background: user image or themed gradient */}
        {card.insideImageUrl ? (
          <img
            src={card.insideImageUrl}
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: card.theme.bgGradient }}>
            {/* Dot pattern overlay */}
            <div style={{ position: 'absolute', inset: 0, opacity: card.theme.overlayOpacity, pointerEvents: 'none' }}>
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="print-inside-dots" x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="0.7" fill="currentColor" opacity="0.2" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#print-inside-dots)" />
              </svg>
            </div>
          </div>
        )}

        {/* Personalization text overlay at bottom */}
        {hasPersonalization && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '0.4in 0.5in',
            background: card.insideImageUrl
              ? 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)'
              : 'transparent',
            color: card.insideImageUrl ? '#ffffff' : card.theme.textColor,
            textAlign: 'center',
            zIndex: 10,
          }}>
            {card.recipientName && (
              <div style={{ marginBottom: '0.08in' }}>
                <div style={{ fontSize: '10px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.15em' }}>To</div>
                <div style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.2, fontStyle: isSerif ? 'italic' : 'normal', textShadow: card.insideImageUrl ? '0 1px 4px rgba(0,0,0,0.4)' : 'none' }}>
                  {card.recipientName}
                </div>
              </div>
            )}

            {card.recipientName && card.message && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: 0.5, marginBottom: '0.08in' }}>
                <div style={{ width: '20px', height: '1px', backgroundColor: 'currentColor' }} />
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: 'currentColor' }} />
                <div style={{ width: '20px', height: '1px', backgroundColor: 'currentColor' }} />
              </div>
            )}

            {card.message && (
              <div style={{ marginBottom: '0.12in', maxWidth: '3.4in', margin: '0 auto 0.12in' }}>
                <p style={{ fontSize: '13px', lineHeight: 1.6, opacity: 0.9, fontStyle: isSerif ? 'italic' : 'normal', textShadow: card.insideImageUrl ? '0 1px 3px rgba(0,0,0,0.4)' : 'none' }}>
                  &ldquo;{card.message}&rdquo;
                </p>
              </div>
            )}

            {card.senderName && (
              <div>
                <div style={{ fontSize: '9px', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.15em' }}>From</div>
                <div style={{ fontSize: '15px', fontWeight: 600, fontStyle: isSerif ? 'italic' : 'normal', textShadow: card.insideImageUrl ? '0 1px 3px rgba(0,0,0,0.4)' : 'none' }}>{card.senderName}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
